import webApiAdmin from '@/lib/api-common/webApiAdmin';
import {
  clearIndexedDbCacheByPrefix,
  registerHeavyCache,
  runCachedFetch,
  getStableCacheValue,
  type CachedFetchOptions,
  type CachedFetchResult,
} from '@/lib/utils/indexedDbCache';

export interface Advertiser {
  _id: string;
  company?: string;
}

type AdvertiserListResponse = Advertiser[] | { advertisers?: Advertiser[] };

export interface GetAdvertisersOptions {
  onlyApproved?: boolean;
}

/** IndexedDB 캐시 키 접두사 — 헤더 TTL 표시·일괄 삭제 등에서 공유 */
export const ADVERTISER_CACHE_KEY_PREFIX = 'advertisers.getAdvertisers::';
const pendingAdvertiserRequests = new Map<string, Promise<Advertiser[]>>();

function getAdvertiserRequestParams(options?: GetAdvertisersOptions): Record<string, unknown> {
  return options ? { onlyApproved: options.onlyApproved } : {};
}

function getAdvertiserCacheKey(options?: GetAdvertisersOptions): string {
  return `${ADVERTISER_CACHE_KEY_PREFIX}${JSON.stringify(
    getStableCacheValue(getAdvertiserRequestParams(options)),
  )}`;
}

export async function clearAdvertiserCache(): Promise<void> {
  pendingAdvertiserRequests.clear();
  await clearIndexedDbCacheByPrefix(ADVERTISER_CACHE_KEY_PREFIX);
}

registerHeavyCache(ADVERTISER_CACHE_KEY_PREFIX, clearAdvertiserCache);

function normalizeAdvertisersPayload(data: AdvertiserListResponse | null | undefined): Advertiser[] {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.advertisers ?? [];
}

/** POST /api3/advertisers/getAdvertisers — cache read with `stale` / `revalidation` metadata. */
function fetchAdvertisers(
  options?: GetAdvertisersOptions,
  fetchOptions: CachedFetchOptions = {},
): Promise<CachedFetchResult<Advertiser[]>> {
  const params = getAdvertiserRequestParams(options) as {
    onlyApproved?: boolean;
  };
  return runCachedFetch<Advertiser[]>({
    key: getAdvertiserCacheKey(options),
    prefix: ADVERTISER_CACHE_KEY_PREFIX,
    pending: pendingAdvertiserRequests,
    fetcher: async () =>
      normalizeAdvertisersPayload(
        await webApiAdmin.post<AdvertiserListResponse>('admin/advertisers/list', params),
      ),
    shouldCache: (advertisers) => advertisers.length > 0,
    options: fetchOptions,
  });
}

/** POST /api3/advertisers/getAdvertisers */
export async function getAdvertisers(
  options?: GetAdvertisersOptions,
  fetchOptions: CachedFetchOptions = {},
): Promise<Advertiser[]> {
  return (await fetchAdvertisers(options, fetchOptions)).value;
}

function toAdvertiserMap(advertisers: Advertiser[]): Record<string, Advertiser> {
  return advertisers.reduce<Record<string, Advertiser>>((acc, advertiser) => {
    if (!advertiser._id) {
      return acc;
    }

    acc[advertiser._id] = advertiser;
    return acc;
  }, {});
}

/** `getAdvertisers` keyed by `_id`, with cache metadata — for batch loaders (`loadCampaignIndexData`). */
export async function fetchAdvertiserMap(
  options?: GetAdvertisersOptions,
  fetchOptions?: CachedFetchOptions,
): Promise<CachedFetchResult<Record<string, Advertiser>>> {
  const result = await fetchAdvertisers(options, fetchOptions ?? {});
  return {
    value: toAdvertiserMap(result.value),
    stale: result.stale,
    revalidation: result.revalidation ? result.revalidation.then(toAdvertiserMap) : null,
  };
}

export async function getAdvertiserMap(
  options?: GetAdvertisersOptions,
  fetchOptions?: CachedFetchOptions,
): Promise<Record<string, Advertiser>> {
  return (await fetchAdvertiserMap(options, fetchOptions)).value;
}
